import { z } from 'zod';

/** Social-link platforms accepted by the current user-profile API. */
export const UserProfileSocialPlatformSchema = z.enum([
  'website',
  'x',
  'telegram',
  'discord',
  'github',
  'linkedin',
  'youtube',
  'instagram',
  'facebook',
  'tiktok',
]);

export const UserProfileSocialLinkSchema = z.object({
  platform: UserProfileSocialPlatformSchema,
  url: z.string(),
});

/** GET /public/users/{id} response data when the authenticated caller is the target. */
export const UserProfileSchema = z.object({
  created_at: z.number(),
  email: z.string(),
  email_verified: z.boolean(),
  id: z.string(),
  is_private: z.boolean(),
  is_following: z.boolean(),
  name: z.string().optional(),
  notif_credit_balance: z.number(),
  social_links: z.array(UserProfileSocialLinkSchema),
  sub_credit_balance: z.number(),
  updated_at: z.number().optional(),
  username: z.string(),
  // Swagger exposes both properties but does not require them.
  bio: z.string().optional(),
  image: z.string().optional(),
});

/** PATCH /user/profile returns account fields without the viewer relationship. */
export const UpdatedUserProfileSchema = UserProfileSchema.omit({ is_following: true });

export const UserProfileResponseSchema = z.object({
  code: z.number(),
  status: z.number(),
  data: UserProfileSchema,
});

export type UserProfile = z.infer<typeof UserProfileSchema>;
export type UserProfileSocialPlatform = z.infer<
  typeof UserProfileSocialPlatformSchema
>;
export type UserProfileSocialLink = z.infer<
  typeof UserProfileSocialLinkSchema
>;
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>;

/**
 * GET /user/dashboard — counters and credit balances for the authenticated
 * user's dashboard.
 *
 * `as_of` is the UTC snapshot time in Unix milliseconds.
 */
export const UserDashboardBotCountsSchema = z.object({
  owned: z.number(),
  followed: z.number(),
  total: z.number(),
});

export const UserDashboardNotificationCountsSchema = z.object({
  successfully_delivered_last_24_hours: z.number(),
  successfully_delivered_all_time: z.number(),
});

export const UserDashboardChannelCountsSchema = z.object({
  count: z.number(),
});

export const UserDashboardCreditBalancesSchema = z.object({
  subscription: z.number(),
  notification: z.number(),
});

export const UserDashboardSchema = z.object({
  as_of: z.number(),
  bots: UserDashboardBotCountsSchema,
  notifications: UserDashboardNotificationCountsSchema,
  channels: UserDashboardChannelCountsSchema,
  credits: UserDashboardCreditBalancesSchema,
});

export const UserDashboardResponseSchema = z.object({
  code: z.number(),
  status: z.number(),
  data: UserDashboardSchema,
});

export type UserDashboardBotCounts = z.infer<
  typeof UserDashboardBotCountsSchema
>;
export type UserDashboardNotificationCounts = z.infer<
  typeof UserDashboardNotificationCountsSchema
>;
export type UserDashboardChannelCounts = z.infer<
  typeof UserDashboardChannelCountsSchema
>;
export type UserDashboardCreditBalances = z.infer<
  typeof UserDashboardCreditBalancesSchema
>;
export type UserDashboard = z.infer<typeof UserDashboardSchema>;
export type UserDashboardResponse = z.infer<
  typeof UserDashboardResponseSchema
>;

/**
 * GET /public/users/{id} — a user profile seen through the caller's lens.
 *
 * The backend varies the payload by who is asking, so only `id`, `username`,
 * `is_private` and `is_following` are guaranteed:
 *  - caller IS the target  → every field below, including the sensitive ones
 *    (`email`, `email_verified`, credit balances, linked socials)
 *  - public target         → the public subset (no email / balances)
 *  - private target        → ONLY `id`, `username`, `is_private`, `is_following`
 *
 * Everything optional is therefore genuinely absent in at least one of those
 * cases — read it defensively rather than assuming the self-shape.
 */
export const PublicUserProfileSchema = z.object({
  // Always present, whatever the caller's relationship to the target
  id: z.string(),
  username: z.string(),
  is_private: z.boolean(),
  /**
   * Whether the authenticated caller follows this user. False for anonymous
   * callers and when viewing your own profile.
   *
   * Required, not optional, and the backend serialises it even when false —
   * false is precisely the case the UI most needs to see. It is also present in
   * the minimal private-target view, so someone who already follows an account
   * that has since gone private can still unfollow it.
   */
  is_following: z.boolean(),

  // Public subset — present for a public target and for self
  bio: z.string().optional(),
  created_at: z.number().optional(),
  image: z.string().optional(),
  social_links: z.array(UserProfileSocialLinkSchema).optional(),
  discord: z.string().optional(),
  telegram: z.string().optional(),
  twitter: z.string().optional(),

  // Self-only — the backend omits these for every other caller
  email: z.string().optional(),
  email_verified: z.boolean().optional(),
  notif_credit_balance: z.number().optional(),
  sub_credit_balance: z.number().optional(),
  telegram_bot_id: z.string().optional(),
  telegram_bot_name: z.string().optional(),
});

export type PublicUserProfile = z.infer<typeof PublicUserProfileSchema>;

/**
 * One row of GET /public/users/{id}/activity or GET /user/following/activity —
 * a change event on a strategy.
 *
 * Redaction is server-side and viewer-dependent: `reason`,
 * `credit_transaction_id` and both `auto_renew_*` fields come back only when
 * the caller IS the target. For anyone else the backend strips them, so they
 * are optional here rather than nullable-required.
 *
 * `created_at` is Unix **milliseconds** (the endpoint documents this
 * explicitly, unlike some other timestamps in this service).
 */
/** Who initiated the activity — the bot owner, or an automated system process. */
export const BotActivityActorTypeSchema = z.enum(['USER', 'SYSTEM']);

/** The bot lifecycle / billing activity that occurred. */
export const BotActivityEventTypeSchema = z.enum([
  'BOT_INITED',
  'BOT_ACTIVATED',
  'BOT_REACTIVATED',
  'BOT_DEACTIVATED',
  'BOT_METADATA_UPDATED',
  'BOT_STRATEGY_UPDATED',
  'BOT_AUTO_RENEW_ENABLED',
  'BOT_AUTO_RENEW_DISABLED',
  'BOT_RENEWAL',
  'BOT_EXPIRED',
  'BOT_DELETED',
]);

export const BotActivityEventSchema = z.object({
  // Always present
  id: z.string(),
  created_at: z.number(),
  event_type: BotActivityEventTypeSchema,
  actor_type: BotActivityActorTypeSchema,
  /** Owner of the bot when this activity was recorded. */
  owner_id: z.string(),
  /** The bot VERSION that produced this event — it may since have been archived. */
  bot_id: z.string(),
  /**
   * Latest non-archived, non-deleted version in the lineage, or null when none
   * exists. This — not `bot_id` — is what a "view strategy" link should target.
   */
  current_bot_id: z.string().nullable(),

  // Present depending on the event kind
  /** Latest known name in the lineage, including archived or deleted versions. */
  bot_name: z.string().nullable().optional(),
  /** Stable lineage identifier, shared across bot versions. */
  original_bot_id: z.string().nullable().optional(),
  status_from: z.string().nullable().optional(),
  status_to: z.string().nullable().optional(),
  expiry_from: z.number().nullable().optional(),
  expiry_to: z.number().nullable().optional(),

  // Owner-only — redacted for every other viewer
  reason: z.string().nullable().optional(),
  credit_transaction_id: z.string().nullable().optional(),
  auto_renew_from: z.boolean().nullable().optional(),
  auto_renew_to: z.boolean().nullable().optional(),
});

/**
 * Cursor page returned by both activity endpoints. A private target, or a
 * network containing only private users, returns an empty `rows` rather than
 * an error, so an empty feed is a legitimate success state.
 */
export const UserActivityDataSchema = z.object({
  rows: z.array(BotActivityEventSchema),
  has_more: z.boolean().optional(),
  next_cursor: z.string().nullable().optional(),
});

export type BotActivityActorType = z.infer<typeof BotActivityActorTypeSchema>;
export type BotActivityEventType = z.infer<typeof BotActivityEventTypeSchema>;
export type BotActivityEvent = z.infer<typeof BotActivityEventSchema>;
export type UserActivityData = z.infer<typeof UserActivityDataSchema>;

/**
 * One row of GET /user/following or GET /user/followers.
 *
 * Both endpoints return the same shape; only the direction of the relationship
 * differs, which is why `followed_at` means "you followed them" on /following
 * and "they followed you" on /followers.
 *
 * Note what is NOT here: no public-strategy count, no follower count, and no
 * flag saying whether the authenticated user follows this person. The last one
 * matters — on /followers there is no way to tell a mutual follow from a
 * one-way one.
 *
 * `followed_at` is Unix milliseconds.
 */
export const UserFollowSummarySchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  image: z.string(),
  bio: z.string(),
  is_private: z.boolean(),
  followed_at: z.number(),
});

/** Cursor page of follow relationships. */
export const UserFollowsDataSchema = z.object({
  rows: z.array(UserFollowSummarySchema),
  has_more: z.boolean().optional(),
  next_cursor: z.string().nullable().optional(),
});

export type UserFollowSummary = z.infer<typeof UserFollowSummarySchema>;
export type UserFollowsData = z.infer<typeof UserFollowsDataSchema>;
